import React, { useEffect, useMemo, useState } from 'react';
import TemplateEditor from '../../components/email/TemplateEditor';
import { TEMPLATE_TYPE_MAP } from '../../services/emailApi';
const STATUS_LABELS: Record<number, string> = { 0: 'Draft', 1: 'Active', 2: 'Inactive' };
import {
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  previewTemplate,
  sendTestTemplate,
  getSenders,
  EmailTemplate,
  EmailSender,
} from '../../services/emailApi';

const defaultTemplate: EmailTemplate = {
  name: '',
  subject: '',
  type: '',
  status: 'Active',
  htmlContent: '',
  variables: ['ClientName','BookingDate','Amount'],
};

const EmailTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [senders, setSenders] = useState<EmailSender[]>([]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, s] = await Promise.all([getTemplates(), getSenders()]);
      setTemplates(Array.isArray(data) ? data : (data?.templates || []));
      setSenders(Array.isArray(s) ? s : (s?.senders || []));
    } catch (e: any) {
      setError(e?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return templates.filter(t => {
      const matchesSearch = !search || t.name?.toLowerCase().includes(search.toLowerCase());
      const matchesType = !typeFilter || t.type === typeFilter;
      const matchesStatus = !statusFilter || (t.status || 'Active') === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [templates, search, typeFilter, statusFilter]);

  const onCreate = () => {
    setEditing({ ...defaultTemplate });
    setShowEditor(true);
  };

  const onEdit = (tpl: EmailTemplate) => {
    setEditing({ ...tpl });
    setShowEditor(true);
  };

  const onClone = (tpl: EmailTemplate) => {
    const copy = { ...tpl };
    delete (copy as any).id;
    copy.name = `${tpl.name} (Copy)`;
    setEditing(copy);
    setShowEditor(true);
  };

  const onDelete = async (tpl: EmailTemplate) => {
    if (!tpl.id) return;
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    await deleteTemplate(tpl.id);
    load();
  };

  const handleSaveChange = async (updated: EmailTemplate) => {
    setEditing(updated);
  };

  const handlePreview = async (tpl: EmailTemplate) => {
    if (!tpl.id) {
      // create temp then preview
      const created = await createTemplate(tpl);
  const rendered = await previewTemplate(created.id!, {});
      const wnd = window.open();
      if (wnd) {
        wnd.document.write(`<html><head><title>${rendered.subject}</title></head><body>${rendered.body}</body></html>`);
        wnd.document.close();
      }
      await load();
      return;
    }
    const rendered = await previewTemplate(tpl.id, {});
    const wnd = window.open();
    if (wnd) {
      wnd.document.write(`<html><head><title>${rendered.subject}</title></head><body>${rendered.body}</body></html>`);
      wnd.document.close();
    }
  };

  const handleSendTest = async (tpl: EmailTemplate, senderId: string, email: string) => {
    if (!tpl.id) {
      const created = await createTemplate(tpl);
  await sendTestTemplate(created.id!, senderId, email, {});
      await load();
      return;
    }
    await sendTestTemplate(tpl.id, senderId, email, {});
  };

  const saveEditor = async () => {
    if (!editing) return;
    if (editing.id) {
      await updateTemplate(editing.id, editing);
    } else {
      await createTemplate(editing);
    }
    setShowEditor(false);
    setEditing(null);
    await load();
  };

  return (
    <div className="p-6 bg-background min-h-screen">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-primary">Email Templates</h1>
        <button onClick={onCreate} className="px-4 py-2 bg-primary text-white rounded">New Template</button>
      </div>

      <div className="bg-white rounded shadow p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          placeholder="Search by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded px-3 py-2 text-sm"
        />
        <input
          placeholder="Type (e.g. BookingConfirmation)"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border rounded px-3 py-2 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border rounded px-3 py-2 text-sm"
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        <button onClick={load} className="px-4 py-2 bg-secondary text-white rounded">Refresh</button>
      </div>

      {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="bg-white rounded shadow overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Last Used</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id || t.name} className="border-t">
                  <td className="p-2">{t.name}</td>
                  <td className="p-2">
                    {(() => {
                      // Show type as string (not number)
                      if (typeof t.type === 'string') return t.type;
                      // If type is a number, map to string
                      const typeNum = typeof t.type === 'number' ? t.type : Number(t.type);
                      const typeEntry = Object.entries(TEMPLATE_TYPE_MAP).find(([k, v]) => v === typeNum);
                      return typeEntry ? typeEntry[0] : t.type;
                    })()}
                  </td>
                  <td className="p-2">
                    {(() => {
                      if (typeof t.status === 'string' && isNaN(Number(t.status))) return t.status;
                      const statusNum = typeof t.status === 'number' ? t.status : Number(t.status);
                      return STATUS_LABELS[statusNum] || t.status;
                    })()}
                  </td>
                  <td className="p-2">{t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString() : '-'}</td>
                  <td className="p-2 space-x-2">
                    <button onClick={() => onEdit(t)} className="text-primary hover:underline">Edit</button>
                    <button onClick={() => onClone(t)} className="text-blue-600 hover:underline">Clone</button>
                    {t.id && <button onClick={() => handlePreview(t)} className="text-green-600 hover:underline">Preview</button>}
                    {t.id && (
                      <button
                        onClick={() => {
                          const email = prompt('Send test to email:') || '';
                          const defaultSenderId = senders[0]?.id ? String(senders[0].id) : '';
                          if (!email) return;
                          if (!defaultSenderId) {
                            alert('No sender configured. Please add an Email Sender first.');
                            return;
                          }
                          handleSendTest(t, defaultSenderId, email);
                        }}
                        className="text-secondary hover:underline"
                      >
                        Send Test
                      </button>
                    )}
                    {t.id && <button onClick={() => onDelete(t)} className="text-red-600 hover:underline">Delete</button>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td className="p-4" colSpan={5}>No templates found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showEditor && editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow max-w-4xl w-full p-4">
            <TemplateEditor
              template={editing}
              variables={editing.variables || defaultTemplate.variables!}
              onChange={handleSaveChange}
              onPreview={handlePreview}
              onSendTest={handleSendTest}
              senders={senders}
              onClose={() => { setShowEditor(false); setEditing(null); }}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setShowEditor(false); setEditing(null); }} className="px-4 py-2 rounded border">Cancel</button>
              <button onClick={saveEditor} className="px-4 py-2 rounded bg-primary text-white">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesPage;
